<template>
  <div class="table-container">
    <div class="table-header">
      <el-button class="button-background-color" @click="handleAdd">
        <el-icon><Plus/></el-icon>
        {{ $t('SerialNumberUpgrade.add') }}
      </el-button>
    </div>
    
    <!-- 序列号升级表格 -->
    <el-table
      :data="tableData"
      style="width: 100%"
      border
      :loading="loading"
      @selection-change="handleSelectionChange"
    >
      <el-table-column :label="$t('SerialNumberUpgrade.index')" width="60">
        <template #default="{ row }">
          {{ row.id }}
        </template>
      </el-table-column>
      <el-table-column prop="name" :label="$t('SerialNumberUpgrade.name')" show-overflow-tooltip />
      <el-table-column prop="firmwareFile" :label="$t('SerialNumberUpgrade.firmwareFile')" show-overflow-tooltip>
        <template #default="{ row }">
          <span v-if="row.firmwareFile">
            {{ row.firmwareFile }}
          </span>
        </template>
      </el-table-column>
      <el-table-column prop="paramsFile" :label="$t('SerialNumberUpgrade.parameterFile')" show-overflow-tooltip>
        <template #default="{ row }">
          <span v-if="row.paramsFile">
            {{ row.paramsFile }}
          </span>
        </template>
      </el-table-column>
      <el-table-column prop="description" :label="$t('SerialNumberUpgrade.description')" show-overflow-tooltip />
      <el-table-column :label="$t('SerialNumberUpgrade.action')" fixed="right" width="280">
        <template #default="{ row }">
          <el-button size="small" @click="handleView(row)">
            {{ $t('SerialNumberUpgrade.view') }}
          </el-button>
          <el-button type="primary" size="small" @click="handleEdit(row)">
            {{ $t('SerialNumberUpgrade.edit') }}
          </el-button>
          <el-button type="success" size="small" @click="handleImport(row)">
            <el-icon><Upload /></el-icon>
            {{ $t('SerialNumberUpgrade.import') }}
          </el-button>
          <el-button type="danger" size="small" @click="handleDelete(row.id)">
            {{ $t('SerialNumberUpgrade.delete') }}
          </el-button>
        </template>
      </el-table-column>
    </el-table>

    <!-- 添加/编辑/查看对话框 -->
    <el-dialog 
      v-model="dialogVisible" 
      :title="getDialogTitle()"
      width="800px"
      :before-close="resetFormData"
      :close-on-click-modal="false"
    >
      <!-- 加载状态 -->
      <div v-if="detailLoading" class="loading-state">
        <el-loading :visible="true" text="正在加载数据..." />
      </div>
      
      <!-- 编辑或添加表单 -->
      <div v-else-if="!isView" class="form-container">
        <el-form ref="formRef" :model="formData" :rules="formRules" label-width="120px">
          <el-form-item :label="$t('SerialNumberUpgrade.name')" prop="name">
            <el-input v-model="formData.name" :placeholder="$t('SerialNumberUpgrade.namePlaceholder')"></el-input>
          </el-form-item>
          
          <el-form-item :label="$t('SerialNumberUpgrade.firmwareFile')">
            <el-upload
              ref="firmwareUpload"
              class="firmware-upload"
              :action="''"
              :http-request="handleFirmwareUpload"
              :show-file-list="true"
              :file-list="firmwareFileList"
              accept=".bin,.zip"
              :disabled="isView"
              :on-remove="handleFirmwareRemove"
            >
              <el-button size="small" type="primary">
                {{ $t('SerialNumberUpgrade.uploadBtn') }}
              </el-button>
              <template #tip>
                <div class="el-upload__tip">
                  {{ $t('SerialNumberUpgrade.firmwareFileTip') }}
                </div>
              </template>
            </el-upload>
          </el-form-item>
          
          <el-form-item :label="$t('SerialNumberUpgrade.parameterFile')">
            <el-upload
              ref="parameterUpload"
              class="parameter-upload"
              :action="''"
              :http-request="handleParameterUpload"
              :show-file-list="true"
              :file-list="parameterFileList"
              accept=".bin,.zip"
              :disabled="isView"
              :on-remove="handleParameterRemove"
            >
              <el-button size="small" type="primary">
                {{ $t('SerialNumberUpgrade.uploadBtn') }}
              </el-button>
              <template #tip>
                <div class="el-upload__tip">
                  {{ $t('SerialNumberUpgrade.parameterFileTip') }}
                </div>
              </template>
            </el-upload>
          </el-form-item>
          
          <el-form-item :label="$t('SerialNumberUpgrade.description')">
            <el-input
              v-model="formData.description"
              type="textarea"
              :placeholder="$t('SerialNumberUpgrade.descriptionPlaceholder')"
              :disabled="isView"
            ></el-input>
          </el-form-item>
        </el-form>
      </div>
      
      <!-- 查看模式下显示序列号列表 -->
      <div v-else>
        <div v-if="serialNumbers.length > 0">
          <div class="serial-numbers-container">
            <table class="serial-numbers-table">
              <thead>
                <tr>
                  <th class="serial-number-header-index">序号</th>
                  <th class="serial-number-header-value">序列号</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(serialNumber, index) in serialNumbers" :key="index">
                  <td class="serial-number-index">{{ index + 1 + (currentPage - 1) * pageSize }}</td>
                  <td class="serial-number-value">{{ serialNumber }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          
          <!-- 分页组件 -->
          <div class="serial-numbers-pagination">
            <el-pagination
              v-model:current-page="currentPage"
              v-model:page-size="pageSize"
              :page-sizes="[10, 20, 50, 100]"
              layout="total, sizes, prev, pager, next, jumper"
              :total="totalSerialNumbers"
              @size-change="handleSizeChange"
              @current-change="handlePageChange"
            />
          </div>
        </div>
        
        <!-- 无序列号状态 -->
        <div v-else class="no-serial-numbers">
          <div class="no-data-icon">
            <el-icon><Document /></el-icon>
          </div>
          <div class="no-data-text">
            <p>{{ $t('SerialNumberUpgrade.noSerialNumbersFound') }} ({{ selectedId }})</p>
            <p class="sub-text">{{ $t('SerialNumberUpgrade.checkIdOrImport') }}</p>
          </div>
        </div>
      </div>
      
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="resetFormData">
            {{ $t('SerialNumberUpgrade.cancel') }}
          </el-button>
          <el-button 
            type="primary"
            @click="submitForm"
            :loading="submitLoading"
            v-if="!isView"
          >
            {{ $t('SerialNumberUpgrade.confirm') }}
          </el-button>
        </span>
      </template>
    </el-dialog>

    <!-- 导入对话框 -->
    <el-dialog 
      v-model="importDialogVisible" 
      :title="$t('SerialNumberUpgrade.importTitle')"
      width="600px"
    >
      <el-upload 
        :action="''" 
        :http-request="handleImportFileUpload"
        :show-file-list="true"
        :file-list="importFileList"
        accept=".csv,.xlsx,.xls"
      >
        <el-button size="small" type="primary">
          {{ $t('SerialNumberUpgrade.uploadBtn') }}
        </el-button>
        <template #tip>
          <div class="el-upload__tip">
            {{ $t('SerialNumberUpgrade.importFileTip') }}
          </div>
        </template>
      </el-upload>
      
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="importDialogVisible = false">
            {{ $t('SerialNumberUpgrade.cancel') }}
          </el-button>
          <el-button 
            type="primary" 
            @click="submitImport"
            :disabled="!importFile || isUploading"
            :loading="importLoading || isUploading"
          >
            {{ $t('SerialNumberUpgrade.confirm') }}
          </el-button>
        </span>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ElMessage } from 'element-plus';
import { Plus, Upload, Document } from '@element-plus/icons-vue';
import {
  addSerialNumberUpgrade,
  updateSerialNumberUpgrade,
  getSerialNumberUpgradeDetail,
  importSerialNumberUpgrades,
  uploadFileData,
  getSerialNumberUpgradeDevices
} from '@/api/api';

export default {
  components: {
    Plus,
    Upload,
    Document
  },
  props: {
    tableData: {
      type: Array,
      required: true
    }, 
    loading: {
      type: Boolean,
      default: false
    }
  },
  data() {
      return {
        dialogVisible: false,
        importDialogVisible: false,
        isEdit: false,
        isView: false,
        selectedId: null,
        importRelatedId: null,
        submitLoading: false,
        importLoading: false,
        detailLoading: false, // 用于详情加载的本地状态
        importFile: null,
        firmwareFileList: [],
        parameterFileList: [],
        serialNumbers: [],
        totalSerialNumbers: 0,
        currentPage: 1,
        pageSize: 10,
        importFileList: [],
        isUploading: false, // 用于标识上传状态
        formRules: {
          name: [
            { required: true, message: this.$t('SerialNumberUpgrade.nameRequired'), trigger: 'blur' }
          ]
        },
        formData: {
          name: '',
          firmwareFile: '',
          firmwareFileKey: '',
          firmwareFileUrl: '',
          firmwareFileName: '', // 新增字段：固件文件名
          paramsFile: '',
          paramsFileKey: '',
          paramsFileUrl: '',
          paramsFileName: '', // 新增字段：参数文件名
          description: '',
          uploadedFile: '', // 用于提交给后端的文件key
          uploadedParamsFile: '', // 用于提交给后端的参数文件key
          firmwareFileSize: 0,
          paramsFileSize: 0,
          fileList: [] // 用于存储上传的文件列表
        }
      };
    },
  methods: {
    // 获取对话框标题
    getDialogTitle() {
      if (this.isView) {
        return this.$t('SerialNumberUpgrade.view');
      } else if (this.isEdit) {
        return this.$t('SerialNumberUpgrade.edit');
      } else {
        return this.$t('SerialNumberUpgrade.add');
      }
    },

    // 处理新增
    handleAdd() {
      this.isEdit = false;
      this.isView = false;
      this.selectedId = null;
      this.resetFormData();
      this.dialogVisible = true;
    },

    // 处理编辑
    async handleEdit(row) {
      this.isEdit = true;
      this.isView = false;
      this.selectedId = row.id;
      this.detailLoading = true;
      this.dialogVisible = true;
      
      try {
        // 获取详情数据
        const result = await getSerialNumberUpgradeDetail(this.selectedId);
        
        if (result.code === 0 && result.data) {
          const data = result.data;
          
          // 初始化formData
          this.formData = {
            name: data.name || '',
            firmwareFile: data.firmware_file_name || data.firmware_file || '',
            firmwareFileKey: data.firmware_file || '',
            firmwareFileName: data.firmware_file_name || '',
            firmwareFileSize: data.firmware_file_size || 0,
            firmwareFileUrl: data.firmware_file_url || '',
            paramsFile: data.params_file_name || data.params_file || '',
            paramsFileKey: data.params_file || '',
            paramsFileName: data.params_file_name || '',
            paramsFileSize: data.params_file_size || 0,
            paramsFileUrl: data.params_file_url || '',
            description: data.desc || '',
            uploadedFile: data.firmware_file || '',
            uploadedParamsFile: data.params_file || '',
            fileList: []
          };
          
          // 处理固件文件展示
          if (data.firmware_file) {
            this.firmwareFileList = [{
              name: data.firmware_file_name || data.firmware_file,
              url: data.firmware_file_url,
              status: 'success'
            }];
            this.formData.fileList.push({
              name: data.firmware_file_name || data.firmware_file,
              key: data.firmware_file,
              url: data.firmware_file_url || '',
              type: 'firmware'
            });
          } else {
            this.firmwareFileList = [];
          }
          
          // 处理参数文件展示
          if (data.params_file) {
            this.parameterFileList = [{
              name: data.params_file_name || data.params_file,
              url: data.params_file_url,
              status: 'success'
            }];
            this.formData.fileList.push({
              name: data.params_file_name || data.params_file,
              key: data.params_file,
              url: data.params_file_url || '',
              type: 'parameter'
            });
          } else {
            this.parameterFileList = [];
          }
        } else {
          ElMessage.error(this.$t('SerialNumberUpgrade.loadDetailFailed'));
        }
      } catch (error) {
        ElMessage.error(this.$t('SerialNumberUpgrade.loadDetailFailed'));
      } finally {
        this.detailLoading = false;
      }
    },

    // 查看序列号列表
      handleView(row) {
        // 设置视图模式标志
        this.isView = true;
        this.isEdit = false;
        
        // 确保row和row.id存在
        if (!row || !row.id || row.id === null || row.id === undefined || row.id === '') {
          ElMessage.error(this.$t('SerialNumberUpgrade.invalidId'));
          return;
        }
        
        // 先设置selectedId，确保使用正确的ID
        this.selectedId = row.id;
        
        // 重置页码和分页参数
        this.currentPage = 1;
        this.pageSize = 10;
        
        // 先清空旧数据，防止显示上一次的数据
        this.serialNumbers = [];
        this.totalSerialNumbers = 0;
        
        // 设置加载状态
        this.detailLoading = true;
        
        // 显示对话框
        this.dialogVisible = true;
        
        // 然后再获取新数据
        // 使用setTimeout确保UI先更新后再发起请求，避免显示旧数据
        setTimeout(async () => {
          try {
            await this.fetchSerialNumbers();
          } catch (error) {
            ElMessage.error(this.$t('SerialNumberUpgrade.loadSerialNumbersFailed'));
          } finally {
            this.detailLoading = false;
          }
        }, 100);
      },
    
    // 获取序列号列表
    async fetchSerialNumbers() {
      // 验证selectedId有效性
      if (!this.selectedId || this.selectedId === null || this.selectedId === undefined || this.selectedId === '') {
        ElMessage.error(this.$t('SerialNumberUpgrade.invalidId'));
        return;
      }
      
      try {
        this.detailLoading = true;
        

        
        // 调用API，确保传递正确的分页参数
        const response = await getSerialNumberUpgradeDevices(this.selectedId, this.currentPage, this.pageSize);
        
        
        
        // 提取数据字段
        let data = [];
        
        // 处理API响应中的数据
        if (response && typeof response === 'object') {
          // 直接从响应对象中提取所有带serial_no的项
          if (Array.isArray(response)) {
            // 如果响应本身是数组
            data = response;
          } else if (response.data) {
            // 优先处理data字段
            if (Array.isArray(response.data)) {
              data = response.data;
            } else if (typeof response.data === 'object') {
              // 检查meta字段获取总数
              if (response.data.meta && response.data.meta.total) {
                this.totalSerialNumbers = response.data.meta.total;
              }
              
              // 尝试多种可能的数据字段
              if (Array.isArray(response.data.data)) {
                data = response.data.data;
              } else if (Array.isArray(response.data.devices)) {
                data = response.data.devices;
              } else if (Array.isArray(response.data.items)) {
                data = response.data.items;
              } else if (Array.isArray(response.data.list)) {
                data = response.data.list;
              } else {
                // 从response.data对象中直接提取所有带serial_no的属性值
                data = Object.values(response.data).filter(item => 
                  typeof item === 'object' && item !== null && item.serial_no !== undefined
                );
              }
            }
          } else {
            // 尝试从响应对象中直接提取
            data = Object.values(response).filter(item => 
              typeof item === 'object' && item !== null && item.serial_no !== undefined
            );
          }
        }
        
        // 确保data是数组
        if (!Array.isArray(data)) {
          data = [];
        }
        
        
        
        // 提取serial_no字段
        this.serialNumbers = data.map((item) => {
          if (typeof item === 'string') {
            return item;
          } else if (typeof item === 'object' && item !== null) {
            if (item.serial_no) {
              return item.serial_no;
            } else if (item.serialNumber) {
              return item.serialNumber;
            } else if (item.number) {
              return item.number;
            } else {
              return '未知格式';
            }
          } else {
            return '未知类型';
          }
        });
        
        // 确保总数正确设置
        // 优先从meta中获取总数
        let metaTotal = 0;
        
        // 尝试从响应对象的不同位置获取meta.total
        if (response && response.meta && response.meta.total) {
          metaTotal = response.meta.total;
        } else if (response && response.data && response.data.meta && response.data.meta.total) {
          metaTotal = response.data.meta.total;
        }
        
        // 如果找到了meta.total，使用它；否则默认为数据数组的长度
        if (metaTotal > 0) {
          this.totalSerialNumbers = metaTotal;
        } else {
          // 如果API没有返回总数，使用当前数据的长度作为默认值
          this.totalSerialNumbers = data.length;
        }
        
        
        
      } catch (error) {
        
        ElMessage.error(this.$t('SerialNumberUpgrade.loadSerialNumbersFailed'));
      } finally {
        this.detailLoading = false;
      }
    },


    
    // 处理分页变化
    handlePageChange(page) {
      this.currentPage = page;
      this.fetchSerialNumbers();
    },
    
    // 处理每页数量变化
    handleSizeChange(size) {
      this.pageSize = size;
      this.currentPage = 1;
      this.fetchSerialNumbers();
    },

    // 处理删除
    handleDelete(id) {
      this.$confirm(this.$t('SerialNumberUpgrade.confirmDelete'), this.$t('SerialNumberUpgrade.delete'), {
        confirmButtonText: this.$t('SerialNumberUpgrade.confirm'),
        cancelButtonText: this.$t('SerialNumberUpgrade.cancel'),
        type: 'warning'
      }).then(() => {
        this.$emit('delete', id);
      }).catch(() => {
        // 用户取消删除
      });
    },

    // 处理导入
    handleImport(row) {
      this.importRelatedId = row ? row.id : null;
      this.importDialogVisible = true;
    },

    // 统一处理文件上传
      async handleFileUpload(options, fileType) {
        const file = options.file;
        this.isUploading = true; // 标识开始上传
        
        try {
          const formData = new FormData();
          formData.append('file', file);
          formData.append('type', fileType);
          
          // 获取文件大小（从本地文件对象获取）
          const fileSize = file.size;
          
          // 调用上传接口
          const result = await uploadFileData(formData);
          
          if (result.code === 0) {
            // 处理成功的响应
            ElMessage.success(this.$t('ProductManage.uploadSuccess'));
            
            const fileData = result;
            // 清空现有的同类文件
            this.formData.fileList = this.formData.fileList.filter(item => item.type !== fileType);
            
            // 添加新文件到fileList
            this.formData.fileList.push({
              name: fileData.fileName,
              key: fileData.key,
              url: fileData.url,
              type: fileType,
              size: fileSize // 保存文件大小到文件列表
            });
            
            // 根据文件类型更新对应的表单数据
            const isFirmware = fileType === 'firmware';
            const prefix = isFirmware ? 'firmware' : 'params';
            const uploadedKey = isFirmware ? 'uploadedFile' : 'uploadedParamsFile';
            const fileListKey = isFirmware ? 'firmwareFileList' : 'parameterFileList';
            
            // 更新用于提交给后端的参数
            this.formData[uploadedKey] = fileData.key; // 使用key作为文件标识符
            // 优先使用本地获取的文件大小，如果API返回了文件大小也可以使用
            this.formData[`${prefix}FileSize`] = fileSize;
            this.formData[`${prefix}FileName`] = fileData.fileName;
            this.formData[`${prefix}FileUrl`] = fileData.url;
            this.formData[`${prefix}File`] = fileData.fileName; // 更新显示的文件名
            this[fileListKey] = [{ name: fileData.fileName, url: fileData.url, status: 'success', size: fileSize }];
          } else {
            // 处理非成功的响应
            ElMessage.error(this.$t('ProductManage.uploadFailed'));
          }
      } catch (error) {
        ElMessage.error(this.$t('ProductManage.uploadException'));
      } finally {
        this.isUploading = false; // 无论成功或失败，都结束上传状态
      }
      return false;
    },

    // 处理固件文件上传
    handleFirmwareUpload(options) {
      return this.handleFileUpload(options, 'firmware');
    },

    // 处理参数文件上传
    handleParameterUpload(options) {
      return this.handleFileUpload(options, 'parameter');
    },

    // 处理导入文件上传
    handleImportFileUpload(options) {
      try {
        this.isUploading = true;
        const file = options.file;
        
        // 不再调用upload接口，直接保存文件对象
        this.importFile = file;
        this.importFileList = [{ name: file.name, status: 'success' }];
        ElMessage.success(this.$t('SerialNumberUpgrade.uploadSuccess'));
      } catch (error) {
        ElMessage.error(this.$t('SerialNumberUpgrade.uploadException'));
        this.importFile = null;
        this.importFileList = [];
      } finally {
        this.isUploading = false;
      }
      return false; // 阻止默认上传行为
    },

    // 从key中提取文件名
    extractFileNameFromKey(key) {
      if (!key) return null;
      
      // 尝试从key中提取文件名（基于观察到的key格式：9fe76633-1dd8-44a1-9c80-dee49420b7d1/02）
      try {
        // 分割key，获取最后一部分作为文件名
        const parts = key.split('/');
        if (parts.length > 0) {
          // 如果最后一部分看起来像文件ID，可能需要特殊处理
          const lastPart = parts[parts.length - 1];
          
          // 根据之前的网络请求日志，key格式可能类似于：9fe76633-1dd8-44a1-9c80-dee49420b7d1/02）.bin
          // 我们尝试从中提取有意义的文件名
          if (lastPart.includes('.') || lastPart.length <= 4) {
            // 如果包含扩展名或长度较短，可能是文件ID，需要更多上下文来生成有意义的名称
            return 'firmware_' + lastPart; // 简单处理，实际使用中可能需要更复杂的逻辑
          } else {
            return lastPart;
          }
        }
      } catch (error) {
        // 错误处理已在返回值中处理
      }
      
      // 如果提取失败，返回key的最后一部分
      const parts = key.split('/');
      return parts[parts.length - 1];
    },

    // 移除固件文件
    handleFirmwareRemove() {
      this.formData.firmwareFile = '';
      this.formData.firmwareFileKey = '';
      this.formData.firmwareFileUrl = '';
      this.formData.firmwareFileName = '';
      this.formData.uploadedFile = '';
      this.formData.firmwareFileSize = 0;
      this.formData.fileList = this.formData.fileList.filter(item => item.type !== 'firmware');
      this.firmwareFileList = [];
    },

    // 移除参数文件
    handleParameterRemove() {
      this.formData.paramsFile = '';
      this.formData.paramsFileKey = '';
      this.formData.paramsFileUrl = '';
      this.formData.paramsFileName = '';
      this.formData.uploadedParamsFile = '';
      this.formData.paramsFileSize = 0;
      this.formData.fileList = this.formData.fileList.filter(item => item.type !== 'parameter');
      this.parameterFileList = [];
    },

    // 提交表单
    async submitForm() {
      const formRef = this.$refs.formRef;
      try {
        await formRef.validate(async (valid) => {
          if (valid) {
            // 检查文件是否已上传（至少上传一个文件即可）
            if (!this.formData.uploadedFile && !this.formData.uploadedParamsFile) {
              ElMessage.error(this.$t('SerialNumberUpgrade.selectAtLeastOneFile'));
              return;
            }
            
            try {
              this.isUploading = true; // 在提交时设置为上传状态
              
              // 构建提交数据
              const submitData = {
                name: this.formData.name,
                firmware_file: this.formData.uploadedFile, // 使用key提交
                firmware_file_url: this.formData.firmwareFileUrl,
                firmware_file_name: this.formData.firmwareFileName, // 后端新增字段
                firmware_file_size: this.formData.firmwareFileSize,
                params_file: this.formData.uploadedParamsFile, // 使用key提交
                params_file_url: this.formData.paramsFileUrl,
                params_file_name: this.formData.paramsFileName, // 修复字段名，保持一致性
                params_file_size: this.formData.paramsFileSize,
                desc: this.formData.description
              };
              
              if (this.selectedId) {
                await updateSerialNumberUpgrade(this.selectedId, submitData);
                ElMessage.success(this.$t('SerialNumberUpgrade.updateSuccess'));
              } else {
                await addSerialNumberUpgrade(submitData);
                ElMessage.success(this.$t('SerialNumberUpgrade.addSuccess'));
              }
              
              this.dialogVisible = false;
              this.$emit('refresh');
            } catch (error) {
              ElMessage.error(this.selectedId ? this.$t('SerialNumberUpgrade.updateFailed') : this.$t('SerialNumberUpgrade.addFailed'));
            } finally {
              this.isUploading = false; // 提交完成后，结束上传状态
            }
          } else {
            ElMessage.error(this.$t('SerialNumberUpgrade.checkInputValidation'));
          }
        });
      } catch (error) {
        ElMessage.error(this.$t('SerialNumberUpgrade.submitFormError'));
      }
    },

    // 提交导入
    async submitImport() {
      try {
        if (!this.importFile) {
          ElMessage.warning(this.$t('SerialNumberUpgrade.selectFileFirst'));
          return;
        }
        
        this.importLoading = true;
        
        // 构建importData对象，包含文件对象和id
        const importData = {
          auto_upgrade_id: this.importRelatedId,
          file: this.importFile
        };
        
        // 获取导入结果
        const result = await importSerialNumberUpgrades(importData);
        
        // 提取导入统计信息（根据后端实际返回的数据结构）
        const successCount = result.success || 0;
        const failedCount = result.fails || 0;
        const totalCount = result.total || 0;
        
        // 使用MessageBox显示导入结果详情
        await this.$alert(
          `<div style="line-height: 1.8;">
            <p>${this.$t('SerialNumberUpgrade.importResultDetail')}</p>
            <p>总数量: <span style="color: #409EFF;">${totalCount}</span></p>
            <p>成功数量: <span style="color: #67C23A;">${successCount}</span></p>
            <p>失败数量: <span style="color: #F56C6C;">${failedCount}</span></p>
          </div>`,
          result.code === 0 ? this.$t('SerialNumberUpgrade.importSuccess') : this.$t('SerialNumberUpgrade.importFailed'),
          {
            dangerouslyUseHTMLString: true,
            confirmButtonText: this.$t('SerialNumberUpgrade.confirm'),
            type: result.code === 0 ? 'success' : 'warning'
          }
        );
        
        // 用户点击确定后关闭对话框并重置状态
        this.importDialogVisible = false;
        this.importFile = null;
        this.importFileList = [];
        this.importRelatedId = null;
        this.$emit('refresh');
      } catch (error) {
        // 处理可能的错误，包括用户点击取消MessageBox
        if (error.message !== 'cancel') {
          ElMessage.error(this.$t('SerialNumberUpgrade.importFailed'));
        }
      } finally {
        this.importLoading = false;
      }
    },

    // 重置表单数据
    resetFormData() {
      this.formData = {
        name: '',
        firmwareFile: '',
        firmwareFileKey: '',
        firmwareFileUrl: '',
        firmwareFileName: '', // 包含新增字段
        paramsFile: '',
        paramsFileKey: '',
        paramsFileUrl: '',
        paramsFileName: '', // 包含新增字段
        description: '',
        uploadedFile: '', // 用于提交给后端的文件key
        uploadedParamsFile: '', // 用于提交给后端的参数文件key
        firmwareFileSize: 0,
        paramsFileSize: 0,
        fileList: [] // 用于存储上传的文件列表
      };
      this.firmwareFileList = [];
      this.parameterFileList = [];
      this.dialogVisible = false;
      this.selectedId = null;
      this.isEdit = false;
      this.isView = false;
      
      // 清除表单验证
      const formRef = this.$refs.formRef;
      if (formRef) {
        formRef.resetFields();
      }
    },

    // 处理选择变化
    handleSelectionChange() {
      // 此方法为表格选择功能保留，暂不实现具体逻辑
    }
  }
};
</script>

<style scoped>
.table-container {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.table-header {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 16px;
}

.button-background-color {
  background-color: #409EFF;
  border-color: #409EFF;
  color: white;
}

.button-background-color:hover {
  background-color: #66b1ff;
  border-color: #66b1ff;
  color: white;
}

/* 序列号列表样式 */
.serial-numbers-container {
  margin-bottom: 20px;
  border: 1px solid #E4E7ED;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  background-color: white;
}

/* 序列号表格样式 */
.serial-numbers-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  background-color: white;
}

/* 表头样式 */
.serial-numbers-table th {
  background: linear-gradient(135deg, #409EFF 0%, #2979FF 100%);
  padding: 18px 24px;
  text-align: left;
  font-weight: 600;
  color: white;
  font-size: 15px;
  letter-spacing: 0.8px;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  border-right: 1px solid rgba(255, 255, 255, 0.3);
  position: relative;
}

.serial-numbers-table th::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 2px;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
}

.serial-numbers-table th:last-child {
  border-right: none;
}

.serial-number-header-index {
  width: 20%;
  text-align: center !important;
}

.serial-number-header-value {
  width: 80%;
}

/* 单元格样式 */
.serial-numbers-table td {
  padding: 18px 24px;
  text-align: left;
  border-right: 1px solid #F0F2F5;
  border-bottom: 1px solid #F0F2F5;
  font-size: 14px;
  color: #303133;
  transition: all 0.3s ease;
}

.serial-numbers-table td:last-child {
  border-right: none;
}

.serial-numbers-table tr:last-child td {
  border-bottom: none;
}

.serial-number-index {
  width: 20%;
  text-align: center !important;
  font-weight: 600;
  color: #409EFF;
  font-size: 15px;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
}

.serial-number-value {
  width: 80%;
  color: #606266;
  font-size: 15px;
  letter-spacing: 0.5px;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
}

/* 隔行变色 */
.serial-numbers-table tbody tr:nth-child(even) {
  background-color: #FAFCFF;
}

/* 悬停效果 */
.serial-numbers-table tbody tr:hover {
  background-color: #E6F7FF;
  transform: scale(1.003);
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(64, 158, 255, 0.15);
}

.serial-numbers-table tbody tr:hover td {
  border-color: #409EFF;
  color: #303133;
}

/* 分页组件样式 */
.serial-numbers-pagination {
  display: flex;
  justify-content: flex-end;
  margin-top: 24px;
  padding: 16px 0;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  border: 1px solid #E4E7ED;
}

.serial-number-item {
  padding: 4px 8px;
  border-radius: 2px;
  background-color: #FFFFFF;
  border: 1px solid #DCDFE6;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
/* 加载状态样式 */
.loading-state {
  min-height: 200px;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: #FAFAFA;
  border-radius: 4px;
}

/* 无序列号状态样式 - 增强版 */
.no-serial-numbers {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  color: #909399;
  text-align: center;
  padding: 40px 20px;
  background-color: #FAFAFA;
  border: 1px solid #EBEEF5;
  border-radius: 8px;
  transition: all 0.3s ease;
}

.no-data-icon {
  font-size: 48px;
  color: #C0C4CC;
  margin-bottom: 16px;
  opacity: 0.6;
  transition: all 0.3s ease;
}

.no-data-text {
  max-width: 400px;
}

.no-data-text p {
  margin: 0;
  font-size: 16px;
  color: #606266;
  font-weight: 500;
  margin-bottom: 8px;
}

.no-data-text .sub-text {
  font-size: 14px;
  color: #909399;
  font-weight: normal;
  line-height: 1.5;
}

.no-serial-numbers:hover {
  border-color: #DCDFE6;
  background-color: #F8F9FA;
}

.no-serial-numbers:hover .no-data-icon {
  opacity: 0.8;
  transform: scale(1.05);
}

/* 对话框样式增强 */
:deep(.el-dialog__header) {
  border-bottom: 1px solid #EBEEF5;
  padding: 20px 24px;
}

:deep(.el-dialog__body) {
  padding: 24px;
  min-height: 200px;
}
</style>